import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  AlertTriangle,
  Calendar,
  Hash,
  Loader2,
  Mail,
  MapPin,
  Package,
  Phone,
  RefreshCcw,
  Store,
  Tag,
  Truck,
  User,
  X,
} from "lucide-react";
import moment from "moment";
import { OrdersService } from "../../requests/admin/OrdersService";
import OrderStatusBadge from "./OrderStatusBadge";
import { formatCurrency } from "../../utils/formatCurrency";
import { formatLabel } from "../../utils/orderHelpers";

const OrderDetailsModal = ({ isOpen, orderCode, initialOrder, onClose, onUpdateStatus }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [details, setDetails] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (!isOpen || !orderCode) return undefined;

    let isMounted = true;
    setDetails(null);
    setErrorMessage("");

    const fetchDetails = async () => {
      setIsLoading(true);
      const result = await OrdersService.getOrderDetails(orderCode);
      if (!isMounted) return;

      if (result.success && result.data?.status_code === "000" && !result.data?.in_error) {
        setDetails(result.data.data);
      } else {
        setErrorMessage(result.data?.reason || result.message || "Failed to load order details");
      }
      setIsLoading(false);
    };

    fetchDetails();
    return () => {
      isMounted = false;
    };
  }, [isOpen, orderCode]);

  if (!isOpen) return null;

  const summary = details?.summary || initialOrder?.summary;
  const address = details?.delivery_address || initialOrder?.delivery_address;
  const status = details?.status || initialOrder?.status;

  return createPortal(
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="fixed inset-0 z-[1000] backdrop-blur-sm bg-black/40"
      />
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        transition={{ type: "spring", duration: 0.5 }}
        className="flex overflow-auto fixed inset-0 z-[1001] justify-center items-center p-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative my-8 w-full max-w-3xl bg-white rounded-2xl shadow-2xl">
          <div className="flex sticky top-0 z-10 gap-3 items-start px-6 py-4 bg-white rounded-t-2xl border-b border-gray-100">
            <div className="h-9 w-1 bg-gradient-to-b from-[#FF6C6C] to-purple-600 rounded-full flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap gap-2 items-center">
                <h3 className="text-lg font-semibold text-gray-900">Order Details</h3>
                {status && <OrderStatusBadge status={status} />}
              </div>
              <div className="flex gap-1.5 items-center mt-1 text-xs text-gray-500">
                <Hash className="w-3 h-3" />
                <span className="font-mono">{orderCode}</span>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="p-1.5 rounded-full transition-colors hover:bg-gray-100"
              aria-label="Close"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          <div className="px-6 py-5 max-h-[calc(100vh-14rem)] overflow-y-auto">
            {isLoading ? (
              <div className="flex flex-col gap-3 justify-center items-center py-16">
                <Loader2 className="w-7 h-7 text-[#FF6C6C] animate-spin" />
                <p className="text-sm text-gray-500">Loading order details...</p>
              </div>
            ) : errorMessage ? (
              <div className="flex flex-col gap-3 justify-center items-center py-16 text-center">
                <div className="flex justify-center items-center w-12 h-12 bg-red-50 rounded-full">
                  <AlertTriangle className="w-6 h-6 text-red-500" />
                </div>
                <p className="max-w-sm text-sm text-gray-600">{errorMessage}</p>
              </div>
            ) : (
              <div className="space-y-6">
                {(details?.user || initialOrder?.user) && (
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    {[
                      { icon: User, label: "Customer", value: (details?.user || initialOrder?.user)?.full_name },
                      { icon: Mail, label: "Email", value: (details?.user || initialOrder?.user)?.email },
                      { icon: Phone, label: "Phone", value: (details?.user || initialOrder?.user)?.phone_number },
                    ].map(({ icon: Icon, label, value }) => (
                      <div key={label} className="p-3 bg-gradient-to-r from-gray-50 to-white rounded-xl border border-gray-100">
                        <div className="flex gap-2 items-center mb-1 text-gray-400">
                          <Icon className="w-3.5 h-3.5" />
                          <span className="text-[11px] font-semibold tracking-wide uppercase">{label}</span>
                        </div>
                        <p className="text-sm font-medium text-gray-900 truncate">{value || "—"}</p>
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex flex-wrap gap-2">
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-sky-700 bg-sky-50 rounded-full ring-1 ring-sky-600/20">
                    <Truck className="w-3 h-3" />
                    {formatLabel(details?.fulfillment_type || initialOrder?.fulfillment_type)}
                  </span>
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-purple-700 bg-purple-50 rounded-full ring-1 ring-purple-600/20">
                    <Tag className="w-3 h-3" />
                    {formatLabel(details?.purchase_type || initialOrder?.purchase_type)}
                  </span>
                </div>

                {address && (
                  <div className="p-4 bg-gradient-to-br from-gray-50 to-white rounded-xl border border-gray-100">
                    <div className="flex gap-2 items-center mb-2 text-gray-500">
                      <MapPin className="w-4 h-4" />
                      <h4 className="text-sm font-semibold text-gray-700">Delivery Address</h4>
                    </div>
                    <p className="text-sm font-medium text-gray-900">
                      {address.first_name} {address.last_name}
                    </p>
                    <p className="mt-1 text-sm text-gray-600">{address.address}</p>
                    <p className="text-sm text-gray-500">
                      {address.city}, {address.region}
                    </p>
                    {address.delivery_option && (
                      <p className="mt-2 text-xs text-gray-400">
                        Delivery option: {formatLabel(address.delivery_option)}
                      </p>
                    )}
                  </div>
                )}

                <div>
                  <div className="flex gap-2 items-center mb-3">
                    <Package className="w-4 h-4 text-gray-500" />
                    <h4 className="text-sm font-semibold text-gray-700">
                      Order Items ({(details?.items || initialOrder?.items)?.length || 0})
                    </h4>
                  </div>
                  <div className="space-y-3">
                    {(details?.items || initialOrder?.items || []).map((item, idx) => {
                      const size =
                        item.size != null && String(item.size).trim()
                          ? String(item.size).trim()
                          : null;
                      return (
                      <div
                        key={`${item.deal_slug}-${size || "nosize"}-${idx}`}
                        className="flex gap-4 items-start p-3 bg-gray-50/70 rounded-xl border border-gray-100"
                      >
                        <img
                          src={item.deal_image}
                          alt={item.deal_title}
                          className="object-cover flex-shrink-0 w-16 h-16 bg-white rounded-lg border border-gray-200"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = "/images/logo.png";
                          }}
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-gray-900">{item.deal_title}</p>
                          <div className="flex flex-wrap gap-x-4 gap-y-1 items-center mt-1.5 text-xs text-gray-500">
                            {size ? (
                              <span className="inline-flex items-center rounded-md bg-gray-100 px-1.5 py-0.5 text-[11px] font-bold text-gray-700">
                                Size {size}
                              </span>
                            ) : null}
                            <span>
                              Qty <span className="font-medium text-gray-700">{item.quantity}</span>
                            </span>
                            <span>Unit {formatCurrency(item.unit_price)}</span>
                            <span className="font-semibold text-gray-800">{formatCurrency(item.line_total)}</span>
                          </div>
                          {item.vendor && (
                            <div className="flex gap-2 items-center mt-2 p-2 bg-white rounded-lg border border-gray-100">
                              {item.vendor.logo ? (
                                <img src={item.vendor.logo} alt="" className="w-7 h-7 rounded-md object-cover" />
                              ) : (
                                <Store className="w-4 h-4 text-gray-400" />
                              )}
                              <div className="min-w-0">
                                <p className="text-xs font-medium text-gray-800 truncate">{item.vendor.brand_name}</p>
                                <p className="text-[10px] text-gray-400 truncate">{item.vendor.location}</p>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      );
                    })}
                  </div>
                </div>

                {summary && (
                  <div className="p-4 bg-gradient-to-br from-gray-50 to-white rounded-xl border border-gray-100">
                    <p className="mb-3 text-xs font-semibold tracking-wide text-gray-400 uppercase">Order Summary</p>
                    <div className="space-y-1.5 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-500">Subtotal</span>
                        <span className="font-medium">{formatCurrency(summary.subtotal)}</span>
                      </div>
                      {summary.savings > 0 && (
                        <div className="flex justify-between">
                          <span className="text-gray-500">Savings</span>
                          <span className="font-medium text-green-600">-{formatCurrency(summary.savings)}</span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span className="text-gray-500">Delivery Fee</span>
                        <span className="font-medium">
                          {summary.delivery_fee ? formatCurrency(summary.delivery_fee) : "Free"}
                        </span>
                      </div>
                      <div className="flex justify-between pt-2 mt-1 border-t border-gray-200">
                        <span className="font-semibold text-gray-700">Total</span>
                        <span className="text-base font-bold text-gray-900">{formatCurrency(summary.total)}</span>
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex flex-wrap gap-4 items-center px-1 text-xs text-gray-400">
                  <span className="flex gap-1.5 items-center">
                    <Calendar className="w-3.5 h-3.5" />
                    Placed {moment(details?.created_at || initialOrder?.created_at).format("DD MMM YYYY, hh:mm A")}
                  </span>
                </div>
              </div>
            )}
          </div>

          <div className="flex sticky bottom-0 gap-3 justify-end px-6 py-4 bg-white rounded-b-2xl border-t border-gray-100">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-xl transition-colors hover:bg-gray-200"
            >
              Close
            </button>
            {onUpdateStatus && (
              <button
                type="button"
                onClick={onUpdateStatus}
                className="inline-flex gap-2 items-center px-4 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-[#FF6C6C] to-[#FF8F8F] rounded-xl shadow-sm transition-all duration-200 hover:opacity-90 hover:shadow-md"
              >
                <RefreshCcw className="w-4 h-4" />
                Update Status
              </button>
            )}
          </div>
        </div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
};

export default OrderDetailsModal;
